import React, { useCallback, useEffect, useState } from 'react';
import SupplierPageLayout from '../../components/SupplierPageLayout';
import SupplierEmptyState from '../../components/SupplierEmptyState';
import {
  SupplierCatalogItem,
  SupplierOrganization,
  deleteCatalogItem,
  getCatalog,
  getOrganizations,
  importCatalog,
  saveCatalogItem,
} from '../../services/supplierApi';

interface ItemForm {
  id?: number;
  itemName: string;
  supplierSku: string;
  category: string;
  packSize: string;
  listPrice: string;
  hsn: string;
  moq: string;
  leadTimeDays: string;
}

const EMPTY_FORM: ItemForm = {
  itemName: '',
  supplierSku: '',
  category: '',
  packSize: '',
  listPrice: '',
  hsn: '',
  moq: '',
  leadTimeDays: '',
};

const orgLabel = (org: SupplierOrganization) => org.tradeName || org.legalName || `Organisation #${org.id}`;

const toForm = (item: SupplierCatalogItem): ItemForm => ({
  id: item.id,
  itemName: item.itemName ?? '',
  supplierSku: item.supplierSku ?? '',
  category: item.category ?? '',
  packSize: item.packSize != null ? String(item.packSize) : '',
  listPrice: item.listPrice != null ? String(item.listPrice) : '',
  hsn: item.hsn ?? '',
  moq: item.moq != null ? String(item.moq) : '',
  leadTimeDays: item.leadTimeDays != null ? String(item.leadTimeDays) : '',
});

const optionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const formatPrice = (value?: number) =>
  value != null
    ? `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const Catalog: React.FC = () => {
  const [orgs, setOrgs] = useState<SupplierOrganization[]>([]);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [items, setItems] = useState<SupplierCatalogItem[]>([]);
  const [form, setForm] = useState<ItemForm | null>(null);
  const [csv, setCsv] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    getOrganizations()
      .then((data) => {
        const list: SupplierOrganization[] = data.organizations ?? [];
        setOrgs(list);
        setOrgId(list.length > 0 ? list[0].id : null);
        if (list.length === 0) setLoading(false);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const loadItems = useCallback((id: number) => {
    setLoading(true);
    return getCatalog(id)
      .then((data) => setItems(data.items ?? []))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (orgId != null) loadItems(orgId);
  }, [orgId, loadItems]);

  const updateField = (field: keyof ItemForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || orgId == null) return;
    setError('');
    setNotice('');
    if (!form.itemName.trim()) {
      setError('Item name is required.');
      return;
    }
    const price = optionalNumber(form.listPrice);
    if (price == null || price < 0) {
      setError('Enter a valid price (0 or more).');
      return;
    }
    setBusy(true);
    try {
      await saveCatalogItem(orgId, {
        id: form.id,
        itemName: form.itemName.trim(),
        supplierSku: form.supplierSku.trim() || undefined,
        category: form.category.trim() || undefined,
        packSize: optionalNumber(form.packSize),
        listPrice: price,
        hsn: form.hsn.trim() || undefined,
        moq: optionalNumber(form.moq),
        leadTimeDays: optionalNumber(form.leadTimeDays),
      });
      setNotice(form.id ? 'Item updated.' : 'Item added.');
      setForm(null);
      await loadItems(orgId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item: SupplierCatalogItem) => {
    if (orgId == null || item.id == null) return;
    if (!window.confirm(`Remove "${item.itemName}" from your catalog?`)) return;
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await deleteCatalogItem(orgId, item.id);
      setNotice('Item removed.');
      await loadItems(orgId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    if (orgId == null || !csv.trim()) return;
    setError('');
    setNotice('');
    setBusy(true);
    try {
      const data = await importCatalog(orgId, csv);
      setNotice(`Imported ${data.imported ?? 0} item(s). Rows with a missing name or invalid price were skipped.`);
      setCsv('');
      setShowImport(false);
      await loadItems(orgId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleCsvFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then(setCsv).catch(() => setError('Could not read the file.'));
  };

  const toolbar = orgId != null && (
    <div className="sp-actions" style={{ marginTop: 0 }}>
      {orgs.length > 1 && (
        <select
          className="sp-input"
          style={{ maxWidth: 240 }}
          value={orgId}
          onChange={(e) => setOrgId(Number(e.target.value))}
          aria-label="Organisation"
        >
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {orgLabel(o)}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        className="sp-btn sp-btn--primary"
        disabled={busy}
        onClick={() => {
          setShowImport(false);
          setForm({ ...EMPTY_FORM });
        }}
      >
        <i className="fa fa-plus" aria-hidden="true" /> Add item
      </button>
      <button
        type="button"
        className="sp-btn sp-btn--secondary"
        disabled={busy}
        onClick={() => {
          setForm(null);
          setShowImport((v) => !v);
        }}
      >
        <i className="fa fa-upload" aria-hidden="true" /> Import CSV
      </button>
    </div>
  );

  return (
    <SupplierPageLayout
      wide
      title="Catalog"
      subtitle="Manage catalog items, pricing, and bulk CSV import for marketplace visibility."
      icon="fa-list-alt"
      breadcrumbs={[
        { label: 'Dashboard', to: '/supplier/dashboard' },
        { label: 'Catalog' },
      ]}
    >
      {error && <div className="sp-alert sp-alert--error">{error}</div>}
      {notice && <div className="sp-alert sp-alert--success">{notice}</div>}

      {!loading && orgs.length === 0 ? (
        <SupplierEmptyState
          icon="fa-list-alt"
          title="No supplier organisation yet"
          message="Register your business as a supplier to start publishing catalog items."
        />
      ) : (
        <>
          {toolbar}

          {form && (
            <form className="sp-card" style={{ marginTop: 16 }} onSubmit={handleSave}>
              <div className="sp-card__header">
                <strong>{form.id ? 'Edit item' : 'New item'}</strong>
              </div>
              <div className="sp-card__body" style={{ display: 'flex', flexWrap: 'wrap', columnGap: 16 }}>
                <div className="sp-form-row">
                  <label>
                    Item name *
                    <input className="sp-input" style={{ maxWidth: 240 }} value={form.itemName} onChange={updateField('itemName')} required />
                  </label>
                </div>
                <div className="sp-form-row">
                  <label>
                    Price (₹) *
                    <input className="sp-input" type="number" min="0" step="0.01" value={form.listPrice} onChange={updateField('listPrice')} required />
                  </label>
                </div>
                <div className="sp-form-row">
                  <label>
                    SKU
                    <input className="sp-input" value={form.supplierSku} onChange={updateField('supplierSku')} />
                  </label>
                </div>
                <div className="sp-form-row">
                  <label>
                    Category
                    <input className="sp-input" value={form.category} onChange={updateField('category')} />
                  </label>
                </div>
                <div className="sp-form-row">
                  <label>
                    Pack size
                    <input className="sp-input" type="number" min="0" step="any" value={form.packSize} onChange={updateField('packSize')} />
                  </label>
                </div>
                <div className="sp-form-row">
                  <label>
                    Min. order qty
                    <input className="sp-input" type="number" min="0" step="any" value={form.moq} onChange={updateField('moq')} />
                  </label>
                </div>
                <div className="sp-form-row">
                  <label>
                    Lead time (days)
                    <input className="sp-input" type="number" min="0" step="1" value={form.leadTimeDays} onChange={updateField('leadTimeDays')} />
                  </label>
                </div>
                <div className="sp-form-row">
                  <label>
                    HSN
                    <input className="sp-input" value={form.hsn} onChange={updateField('hsn')} />
                  </label>
                </div>
                <div className="sp-actions" style={{ width: '100%' }}>
                  <button type="submit" className="sp-btn sp-btn--primary" disabled={busy}>
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className="sp-btn sp-btn--secondary" disabled={busy} onClick={() => setForm(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}

          {showImport && (
            <div className="sp-card" style={{ marginTop: 16 }}>
              <div className="sp-card__header">
                <strong>Import from CSV</strong>
                <div className="sp-muted" style={{ marginTop: 4, fontSize: 12 }}>
                  One item per line: <code>name,price,sku</code>. A first line starting with &quot;item&quot; is
                  treated as a header.
                </div>
              </div>
              <div className="sp-card__body">
                <input type="file" accept=".csv,text/csv,text/plain" onChange={handleCsvFile} />
                <textarea
                  className="sp-input"
                  style={{ maxWidth: '100%', height: 140, padding: 10, marginTop: 12, display: 'block' }}
                  placeholder={'item,price,sku\nTomato,40,TOM-1\nOnion,30,ONI-1'}
                  value={csv}
                  onChange={(e) => setCsv(e.target.value)}
                />
                <div className="sp-actions">
                  <button type="button" className="sp-btn sp-btn--primary" disabled={busy || !csv.trim()} onClick={handleImport}>
                    {busy ? 'Importing…' : 'Import'}
                  </button>
                  <button type="button" className="sp-btn sp-btn--secondary" disabled={busy} onClick={() => setShowImport(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="sp-loading">Loading catalog…</div>
          ) : items.length === 0 ? (
            <SupplierEmptyState
              icon="fa-list-alt"
              title="Your catalog is empty"
              message="Add items one by one or import a CSV so restaurants can see what you sell."
            />
          ) : (
            <div className="sp-table-wrap" style={{ marginTop: 16 }}>
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Pack size</th>
                    <th>Price</th>
                    <th>MOQ</th>
                    <th>Lead time</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.itemName}</td>
                      <td>{item.supplierSku || '—'}</td>
                      <td>{item.category || '—'}</td>
                      <td>{item.packSize ?? '—'}</td>
                      <td>{formatPrice(item.listPrice)}</td>
                      <td>{item.moq ?? '—'}</td>
                      <td>{item.leadTimeDays != null ? `${item.leadTimeDays} d` : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button
                          type="button"
                          className="sp-btn sp-btn--secondary"
                          disabled={busy}
                          onClick={() => {
                            setShowImport(false);
                            setForm(toForm(item));
                          }}
                        >
                          Edit
                        </button>{' '}
                        <button type="button" className="sp-btn sp-btn--secondary" disabled={busy} onClick={() => handleDelete(item)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </SupplierPageLayout>
  );
};

export default Catalog;
